'use client';

import React from 'react';
import Link from 'next/link';
import Button from '@mui/material/Button';
import { usePathname } from 'next/navigation';
import IconButton from '@mui/material/IconButton';
import Logo from '@/components/layout/Logo';
import MobilePopover from '@/components/mobile/MobilePopover';
import SvgNew from './SvgNew';
import SvgHamburgerMenu from '@/components/icons/SvgHamburgerMenu';
import {
  DEFAULT_CANDIDATES_ME_ROUTER_PATH,
  CULTURAL_ROUTER_PATH,
  HOME_PAGE_ROUTER_PATH,
  JOB_SALARY_INSIGHT_ROUTER_PATH,
  LOGIN_ROUTER_PATH,
  PRIVACY_ROUTER_PATH,
  RESUME_CONSULTATION_ROUTER_PATH,
  SITEMAP_ROUTER_PATH,
  SUBSCRIBE_ROUTER_PATH,
  TERMS_ROUTER_PATH,
  RESUME_EVALUATION_ROUTER_PATH,
} from '@/constants/router-path';
import {
  REFERRER_STORAGE_NAME,
  HAS_SEEN_NEW_LABEL_COOKIE_NAME,
} from '@/constants/site-track';
import { QUERY_REDIRECT } from '@/constants/auth';
import { HEADER_ID } from '@/constants/component-id';
import GlobalContext from '@/context/global-context';
import { trackClickEvent } from '@/utils/site-track';
import { useClintMediaQuery } from '@/utils/hooks/useClintMediaQuery';

const Header = (props: {
  isSalaryLink?: boolean;
  isHomePage?: boolean;
  isHideHeader?: boolean;
}) => {
  const { isSalaryLink, isHomePage, isHideHeader } = props;
  const isMobile = useClintMediaQuery();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [scrolled, setScrolled] = React.useState(false);
  const pathname = usePathname();

  const [showNewLabel, setShowNewLabel] = React.useState(false);

  const handleClickDropdownMenu = (e: React.MouseEvent<HTMLElement>) => {
    anchorEl ? setAnchorEl(null) : setAnchorEl(e.currentTarget);
  };

  React.useEffect(() => {
    setAnchorEl(null);
  }, [isMobile]);

  React.useEffect(() => {
    const referer = GlobalContext.getContext(REFERRER_STORAGE_NAME);
    const refererPathname = referer ? new URL(referer).pathname : null;
    setShowNewLabel(
      !document.cookie.includes(HAS_SEEN_NEW_LABEL_COOKIE_NAME) &&
        (!refererPathname || refererPathname !== HOME_PAGE_ROUTER_PATH),
    );
    const handleScroll = () => {
      setScrolled(window.scrollY > 1);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      id={HEADER_ID}
      className={`fixed top-0 w-full z-[1100] min-w-[375px] h-16 px-5 items-center justify-between whitespace-nowrap ${isHomePage && !scrolled ? 'bg-web-purple-100' : 'bg-white'} ${isHideHeader ? 'hidden' : 'flex'} tablet:px-16`}
    >
      <Logo href={HOME_PAGE_ROUTER_PATH} />
      <div className="flex items-center h-full text-sm tablet:text-base">
        {!isMobile && (
          <div className="h-full items-center hidden tablet:flex">
            <Link
              data-selected={pathname === HOME_PAGE_ROUTER_PATH}
              href={HOME_PAGE_ROUTER_PATH}
              className="h-full mr-2 px-3 flex items-center border-b border-b-transparent data-[selected=true]:text-web-blue-500 data-[selected=true]:font-semibold data-[selected=true]:border-b-web-blue-500 hover:text-web-blue-500 hover:border-b hover:border-b-web-blue-500 hover:font-semibold"
              data-ele-group="navigation"
              data-ele-id="btn_简历诊断"
              onClick={(e) => {
                trackClickEvent(e, true);
                if (!isHomePage) {
                  document.cookie = `${HAS_SEEN_NEW_LABEL_COOKIE_NAME};path=/;max-age=31536000`;
                }
              }}
            >
              简历诊断
              {!isHomePage && showNewLabel && <SvgNew className="ml-1" />}
            </Link>
            <Link
              data-selected={pathname === RESUME_CONSULTATION_ROUTER_PATH}
              href={RESUME_CONSULTATION_ROUTER_PATH}
              className="h-full mr-2 px-3 flex items-center border-b border-b-transparent data-[selected=true]:text-web-blue-500 data-[selected=true]:font-semibold data-[selected=true]:border-b-web-blue-500 hover:text-web-blue-500 hover:border-b hover:border-b-web-blue-500 hover:font-semibold"
              data-ele-group="navigation"
              data-ele-id="btn_简历咨询"
              onClick={(e) => trackClickEvent(e, true)}
            >
              简历咨询
            </Link>
            <Link
              data-selected={
                pathname === JOB_SALARY_INSIGHT_ROUTER_PATH ||
                (isSalaryLink &&
                  pathname &&
                  !pathname?.startsWith(SITEMAP_ROUTER_PATH) &&
                  !pathname?.startsWith(PRIVACY_ROUTER_PATH) &&
                  !pathname?.startsWith(TERMS_ROUTER_PATH) &&
                  !pathname?.startsWith(SUBSCRIBE_ROUTER_PATH) &&
                  !pathname?.startsWith(RESUME_EVALUATION_ROUTER_PATH))
              }
              href={JOB_SALARY_INSIGHT_ROUTER_PATH}
              className="h-full mr-2 px-3 flex items-center border-b border-b-transparent data-[selected=true]:text-web-blue-500 data-[selected=true]:font-semibold data-[selected=true]:border-b-web-blue-500 hover:text-web-blue-500 hover:border-b hover:border-b-web-blue-500 hover:font-semibold"
              data-ele-group="navigation"
              data-ele-id="btn_薪酬数据"
              onClick={(e) => trackClickEvent(e, true)}
            >
              薪酬数据
            </Link>
            <Link
              data-selected={pathname === CULTURAL_ROUTER_PATH}
              href={CULTURAL_ROUTER_PATH}
              className="h-full px-3 flex items-center border-b border-b-transparent data-[selected=true]:text-web-blue-500 data-[selected=true]:font-semibold data-[selected=true]:border-b-web-blue-500 hover:text-web-blue-500 hover:border-b hover:border-b-web-blue-500 hover:font-semibold"
              data-ele-group="navigation"
              data-ele-id="btn_职场文化偏好"
              onClick={(e) => trackClickEvent(e, true)}
            >
              职场偏好测试
            </Link>
            <div className="ml-3 mr-8 border-r h-6 border-black/[0.06]" />
          </div>
        )}
        <div className="flex items-center h-full">
          <Link
            href={{
              pathname: LOGIN_ROUTER_PATH,
              query: { [QUERY_REDIRECT]: pathname },
            }}
            className="h-full mr-6 px-3 flex items-center hover:text-web-blue-500 hover:border-b hover:border-b-web-blue-500 hover:font-semibold"
            data-ele-group="navigation"
            data-ele-id="btn_登录"
            onClick={(e) => trackClickEvent(e, true)}
          >
            登录
          </Link>
          <Button
            className="!px-4 !py-2"
            variant="outlined"
            LinkComponent={Link}
            href={DEFAULT_CANDIDATES_ME_ROUTER_PATH}
            data-ele-group="navigation"
            data-ele-id="btn_创建简历"
            onClick={(e) => trackClickEvent(e)}
          >
            创建简历
          </Button>
          <IconButton
            className="!ml-6 !p-0 tablet:!hidden"
            aria-label="Menu"
            sx={{
              '& rect': {
                transformOrigin: 'center',
                transition: '0.2s',
              },
              ...(anchorEl && {
                '& rect:first-of-type': {
                  transform: 'translate(6px, 6px) rotateZ(-45deg)',
                },
                '& rect:nth-of-type(2)': {
                  opacity: 0,
                },
                '& rect:last-of-type': {
                  transform: 'translate(6px, -6px) rotateZ(45deg)',
                },
              }),
            }}
            onClick={(e) => handleClickDropdownMenu(e)}
          >
            <SvgHamburgerMenu />
          </IconButton>
        </div>
      </div>
      <MobilePopover anchorEl={anchorEl}>
        <div className="text-sm w-full h-full px-5 pt-2.5">
          <Link
            data-selected={pathname === HOME_PAGE_ROUTER_PATH}
            href={HOME_PAGE_ROUTER_PATH}
            className="block py-5 w-full flex items-center border-b data-[selected=true]:text-web-blue-500 data-[selected=true]:font-semibold"
            data-ele-group="navigation"
            data-ele-id="btn_简历诊断"
            onClick={(e) => trackClickEvent(e, true)}
          >
            简历诊断
            {!isHomePage && showNewLabel && <SvgNew className="ml-1" />}
          </Link>
          <Link
            data-selected={pathname === RESUME_CONSULTATION_ROUTER_PATH}
            href={RESUME_CONSULTATION_ROUTER_PATH}
            className="block py-5 w-full border-b data-[selected=true]:text-web-blue-500 data-[selected=true]:font-semibold"
            data-ele-group="navigation"
            data-ele-id="btn_简历咨询"
            onClick={(e) => trackClickEvent(e, true)}
          >
            简历咨询
          </Link>
          <Link
            data-selected={
              pathname === JOB_SALARY_INSIGHT_ROUTER_PATH || isSalaryLink
            }
            href={JOB_SALARY_INSIGHT_ROUTER_PATH}
            className="block py-5 w-full border-b data-[selected=true]:text-web-blue-500 data-[selected=true]:font-semibold"
            data-ele-group="navigation"
            data-ele-id="btn_薪酬数据"
            onClick={(e) => trackClickEvent(e, true)}
          >
            薪酬数据
          </Link>
          <Link
            data-selected={pathname === CULTURAL_ROUTER_PATH}
            href={CULTURAL_ROUTER_PATH}
            className="block py-5 w-full border-b data-[selected=true]:text-web-blue-500 data-[selected=true]:font-semibold"
            data-ele-group="navigation"
            data-ele-id="btn_职场文化偏好"
            onClick={(e) => trackClickEvent(e, true)}
          >
            职场偏好测试
          </Link>
        </div>
      </MobilePopover>
    </header>
  );
};

export default Header;
